import * as RadixTooltipPrimitive from '@radix-ui/react-tooltip';
import { Fragment, forwardRef, ReactNode, Ref } from 'react';
import styled, { keyframes } from 'styled-components';
import { restrictedZIndexes } from '../../utils';
import { useCellulaProviderContext } from '../provider';
import { theme } from './tooltip.theme';

const slideUpAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateY(-2px);
	}

  to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const slideRightAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateX(-2px);
	}

  to {
		opacity: 1;
		transform: translateX(0);
	}
`;

const slideDownAndFade = keyframes`
  from {
		opacity: 0;
		transform: translateY(2px);
	}

  to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const slideLeftAndFade = keyframes`
   from {
		opacity: 0;
		transform: translateX(2px);
	}

  to {
		opacity: 1;
		transform: translateX(0);
	}
`;

const elements = {
	tooltip: styled(RadixTooltipPrimitive.Content)`
		--textColor: ${theme.getValue('baseTextColor')};
		--background: ${theme.getValue('baseBackground')};
		--arrowMargin: ${theme.getValue('baseArrowMargin')};

		display: flex;
		align-items: center;
		justify-content: center;
		text-align: left;
		padding: ${theme.getValue('basePadding')};
		filter: drop-shadow(${theme.getValue('baseElevation')});
		border-radius: ${theme.getValue('baseBorderRadius')};
		animation-duration: 400ms;
		animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
		will-change: transform, opacity;
		width: auto;
		max-width: 240px;
		background: var(--background);
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};

		&[data-side='top'] {
			margin-bottom: var(--arrowMargin);
		}

		&[data-side='bottom'] {
			margin-top: var(--arrowMargin);
		}

		&[data-side='left'] {
			margin-right: var(--arrowMargin);
		}

		&[data-side='right'] {
			margin-left: var(--arrowMargin);
		}

		@media (prefers-reduced-motion: no-preference) {
			&[data-state='delayed-open'] {
				&[data-side='top'] {
					animation-name: ${slideUpAndFade};
				}
				&[data-side='right'] {
					animation-name: ${slideRightAndFade};
				}
				&[data-side='bottom'] {
					animation-name: ${slideDownAndFade};
				}
				&[data-side='left'] {
					animation-name: ${slideLeftAndFade};
				}
			}
		}

		&[data-variant='dark'] {
			--background: ${theme.getValue('darkBackground')};
			--textColor: ${theme.getValue('darkTextColor')};
		}

		&[data-variant='light'] {
			--background: ${theme.getValue('lightBackground')};
			--textColor: ${theme.getValue('lightTextColor')};
		}

		&[data-variant='interactive'] {
			--background: ${theme.getValue('interactiveBackground')};
			--textColor: ${theme.getValue('interactiveTextColor')};
		}
	`,
	content: styled.div`
		font: ${theme.getValue('baseText')};
		word-wrap: break-word;
		word-break: break-word;
		color: var(--textColor);
		z-index: 1;
	`,
	arrow: styled.div`
		width: ${theme.getValue('arrowSize')};
		height: ${theme.getValue('arrowSize')};
		margin-top: calc((${theme.getValue('arrowSize')} / 2 * -1));
		transform: rotate(45deg);
		border-bottom-right-radius: ${theme.getValue('arrowBorderRadius')};
		z-index: -1;
		background: var(--background);
		visibility: visible;
	`,
};

type TooltipVariant = 'dark' | 'light' | 'interactive';
type TooltipAlign = 'start' | 'center' | 'end';

interface TooltipProps {
	/**
	 * The element to trigger the tooltip. E.g. `<Tooltip><button>My trigger button</button></Tooltip>`.
	 */
	children: ReactNode;

	/**
	 * The text to display in the tooltip.
	 */
	content?: ReactNode;

	/**
	 * Use to set the open state for the tooltip.
	 */
	open?: boolean;

	/**
	 * Use to set if the tooltip should be rendered as open.
	 */
	defaultOpen?: boolean;

	/**
	 * The delay, in milliseconds, before the tooltip will appear when hover the
	 * trigger element.
	 */
	delayDuration?: number;

	/**
	 * The visual horizontal position of the tooltip.
	 * @default center
	 */
	align?: TooltipAlign;

	/**
	 * The visual vertical position of the tooltip.
	 * @default top
	 */
	side?: 'top' | 'bottom';

	/**
	 * The visual representation of the tooltip.
	 * @default dark
	 */
	variant?: TooltipVariant;

	/**
	 * Callback when the tooltip opens/closes.
	 */
	onOpenChange?: (open: boolean) => void;
}

/**
 * ## Tooltip
 *
 * @example
 * ```tsx
 * <Tooltip content='my content'>
 * 	<Button>My trigger</Button>
 * </Tooltip>
 *
 * ```
 *
 * ---
 *
 * Accessibility interactions see:
 * @see https://www.radix-ui.com/docs/primitives/components/tooltip#accessibility
 *
 * For further documentation see:
 * @see https://www.radix-ui.com/docs/primitives/components/tooltip
 */
function CellulaTooltip(
	{
		children,
		content,
		open,
		defaultOpen,
		delayDuration,
		align = 'center',
		variant = 'dark',
		side = 'top',
		onOpenChange,
		...rest
	}: TooltipProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const { portalElement } = useCellulaProviderContext();

	if (content == null) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<RadixTooltipPrimitive.Provider>
			<RadixTooltipPrimitive.Root open={open} defaultOpen={defaultOpen} delayDuration={delayDuration} onOpenChange={onOpenChange}>
				<RadixTooltipPrimitive.Trigger asChild>{children}</RadixTooltipPrimitive.Trigger>

				<RadixTooltipPrimitive.Portal container={portalElement}>
					<elements.tooltip data-variant={variant} side={side} align={align} ref={ref} {...rest}>
						<elements.content>{content}</elements.content>
						<RadixTooltipPrimitive.Arrow asChild>
							<elements.arrow />
						</RadixTooltipPrimitive.Arrow>
					</elements.tooltip>
				</RadixTooltipPrimitive.Portal>
			</RadixTooltipPrimitive.Root>
		</RadixTooltipPrimitive.Provider>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to display a tooltip.
 */
const Tooltip = forwardRef(CellulaTooltip);

export type { TooltipAlign, TooltipProps, TooltipVariant };
export { Tooltip };
